/* eslint-disable @typescript-eslint/no-explicit-any */
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { ChevronRightIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import { Paragraph, tokens } from '@treams/treams-ui';

import { valueFromMetric } from './AnalyticsMetric';
import { TrafficLight } from './TrafficLight';

const MetricChevron = styled.span`
	display: inline-flex;
	align-items: center;
	color: ${tokens.icon.secondary};
	margin-left: ${tokens.gap.element}px;
	flex-shrink: 0;
	transition: color 0.15s ease;
`;

export function displayFloat(value: number) {
	return (Math.round(value * 10) / 10) % 1 === 0 ? value.toFixed(0) : value.toFixed(1);
}

export function displayMetric(
	metric:
		| graphql.AnalyticsIntegerMetric
		| graphql.AnalyticsFloatMetric
		| graphql.AnalyticsPercentageMetric
		| graphql.AnalyticsFractionMetric
		| graphql.AnalyticsConversationTemplateMetric,
	extract: (metric: any) => number | undefined,
) {
	const value = extract(metric);

	switch (metric.__typename) {
		case 'AnalyticsPercentageMetric':
			return value ? `${displayFloat(value * 100)}%` : '—';
		case 'AnalyticsIntegerMetric':
			return value;
		case 'AnalyticsFloatMetric':
			return value ? displayFloat(value) : '—';
		case 'AnalyticsFractionMetric':
		case 'AnalyticsConversationTemplateMetric':
			return `${value} / ${metric.total}`;
	}
}

export function AnalyticsNumberMetric(props: {
	metricGroupID: string;
	metric:
		| graphql.AnalyticsIntegerMetric
		| graphql.AnalyticsFloatMetric
		| graphql.AnalyticsPercentageMetric
		| graphql.AnalyticsFractionMetric
		| (graphql.AnalyticsConversationTemplateMetric & { id: string; threshold: number });
	totalUsers: number;
	details?: string;
	subtitle?: string;
	clickable?: boolean;
}) {
	const { t } = useTranslation();

	const metricValue = valueFromMetric(props.metric);
	return (
		<Box py={1}>
			<Flex alignItems="center" justifyContent="space-between" style={{ gap: tokens.gap.element }}>
				<Box minWidth={0}>
					<Paragraph>
						<strong>{`${displayMetric(props.metric, valueFromMetric)} `}</strong>
						{t(`analytics.metrics.${props.metric.id}`, {
							details: props.details,
						})}
					</Paragraph>
					{props.subtitle && (
						<Paragraph mt={-1}>
							<small>{props.subtitle}</small>
						</Paragraph>
					)}
				</Box>
				<Flex alignItems="center" flexShrink={0}>
					<TrafficLight
						metric={props.metric}
						value={metricValue}
						totalUsers={
							props.metric.id === 'competencies.total'
								? metricValue
								: props.metric.__typename === 'AnalyticsIntegerMetric'
									? props.totalUsers
									: props.metric.__typename === 'AnalyticsFractionMetric' ||
										  props.metric.__typename === 'AnalyticsConversationTemplateMetric'
										? props.metric.total
										: undefined
						}
						threshold={props.metric.threshold}
					/>
					{props.clickable && (
						<MetricChevron data-metric-chevron>
							<ChevronRightIcon size={18} color="currentColor" />
						</MetricChevron>
					)}
				</Flex>
			</Flex>
		</Box>
	);
}
